// Relative (not @/) so the server can import this file too.
import { TRIP_REWARD_CATEGORIES, TRIP_REWARDS_FIELD } from '../constants/tripRewards.js';

/**
 * Complimentary rewards on a trip (src/constants/tripRewards.js). Pure
 * helpers shared by the admin editor, the customer's trip page and Trips
 * list, the reward email (server/trip-reward-email.js) and the concierge's
 * booking context.
 */

const MAX_NAME = 80;

/** The category's display label ("Priority boarding"), or "Other". */
export function tripRewardCategoryLabel(category) {
  return (TRIP_REWARD_CATEGORIES.find((c) => c.id === category) || TRIP_REWARD_CATEGORIES.at(-1)).label;
}

/** A reward's identity: category + name, case-insensitive. Re-adding the same one is not new. */
export function tripRewardKey(reward) {
  return `${reward?.category || 'other'}|${String(reward?.name || '').trim().toLowerCase()}`;
}

/** The valid rewards on a flight_data: named, a known category, de-duplicated. */
export function tripRewardsOf(flightData) {
  const list = Array.isArray(flightData?.[TRIP_REWARDS_FIELD]) ? flightData[TRIP_REWARDS_FIELD] : [];
  const seen = new Set();
  const out = [];
  for (const r of list) {
    const name = String(r?.name || '').trim().slice(0, MAX_NAME);
    if (!name) continue;
    const category = TRIP_REWARD_CATEGORIES.some((c) => c.id === r?.category) ? r.category : 'other';
    const reward = { id: String(r?.id || ''), category, name, added_at: r?.added_at || null };
    const key = tripRewardKey(reward);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(reward);
  }
  return out;
}

/**
 * The editor's rows → what gets saved: blank rows dropped, each kept
 * reward keeps its id and added_at, each new one gets them now.
 */
export function rewardsToSave(rows, { now = () => new Date(), newId } = {}) {
  const makeId = newId || (() => (globalThis.crypto?.randomUUID?.() || `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`));
  const stamped = (rows || []).map((r) => ({
    ...r,
    id: r?.id || makeId(),
    added_at: r?.added_at || now().toISOString(),
  }));
  return tripRewardsOf({ [TRIP_REWARDS_FIELD]: stamped });
}

/** "United Premier Access", "A and B", "A, B and C". */
export function describeTripRewards(rewards) {
  const names = (rewards || []).map((r) => String(r?.name || '').trim()).filter(Boolean);
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
}
